import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import { useRecoilValue } from 'recoil';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteCustomFormResponse } from 'api/CustomForms';
import { createMedication, deleteMedication, upsertWeightLossSync } from 'api/Medication';

import { iaRa } from 'lib/helpers/utility';
import { GENERIC_ERROR } from 'lib/constants/messages';
import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';

import Input from 'components/shared/Input/Input';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import Select from 'components/shared/Select/Select';
import Textarea from 'components/shared/Textarea/Textarea';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import { Alert, showAlert } from 'components/shared/Alert/Alert';
import RadioButton from 'components/shared/RadioButton/RadioButton';
import SearchProviders from 'components/Search/SearchProviders/SearchProviders';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import { clinicalNote as clinicalNoteState, permissions } from 'components/state';
import {
  getPatientPracticeOptions,
  getPrefilledPatientPractice
} from 'components/Patient/medications/lib/helpers';

import { isMedicationReadOnly } from '../../lib/aggregatedDataHelper';
import { ProviderMedicationSchema } from '../lib/providerMedicationSchema';
import { setAggregateNarrativeState } from '../../Provider/lib/aggregateNarrativeStates';
import SelectMedicationTable from '../../CurrentMedications/components/SelectMedicationTable';
import { translateMedicationToWeightLossDatav2 } from '../../Provider/WeightLoss/lib/helpers';
import {
  MEDICATION_STATUS,
  PATIENT_PORTAL_MEDICATION_READ_ONLY_MESSAGE,
  UNIT_OPTIONS,
  VERADIGM_MEDICATION_READ_ONLY_MESSAGE
} from '../lib/constants';
import {
  createObjectWithKeys,
  formatMedicationDataForUpsert,
  getMedicationDeleteContextMessage,
  injectionRoutes,
  medicationRouteOptions
} from '../lib/helpers';

import { handleIsPatientCase } from './helper';

const UpsertProviderMedication = ({
  showModal = false,
  hideModal = () => null,
  disabled = false,
  fromWeightLoss = false,
  showPreviewMedicationFromWhl = false,
  setShowPreviewMedicationFromWhl = () => {},
  patientId: patientIdProp = null,
  practiceOptions = [],
  isNonERXPrescription = false,
  returnValues = () => {},
  diffDosView = false,
  renderInline = false
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  const clinicalNote = useRecoilValue(clinicalNoteState);
  const userPermissions = useRecoilValue(permissions);

  const {
    setOverviewData = () => {},
    noteInactiveState,
    saveNoteRef
  } = useClinicalNoteContext() || {};

  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientId, appointmentId } = useParams();

  const narrativePatientId = patientIdProp !== null ? String(patientIdProp) : patientId;

  const queryClient = useQueryClient();

  const lastShowModalRef = useRef(showModal);
  useEffect(() => {
    if (showModal?.open) lastShowModalRef.current = showModal;
  }, [showModal]);
  const activeShowModal = showModal?.open ? showModal : lastShowModalRef.current;

  const isVeradigmMedication = Boolean(activeShowModal?.data?.veradigm_id);
  const isPatientPortal = location.pathname.includes('/myportal');

  const medicationReadOnly = isMedicationReadOnly({
    canUserUpdatePatientDocuments: userPermissions?.form?.update_patient_documents,
    data: activeShowModal?.data,
    noteLocked: clinicalNote?.locked || noteInactiveState,
    fromPatientPortal: patientIdProp
  });
  const hasExistingMedication = Boolean(activeShowModal?.data?.id || activeShowModal?.data?.cfr_id);
  const modalTitle = isVeradigmMedication
    ? 'View medication'
    : hasExistingMedication
      ? 'Update medication'
      : 'Create medication';
  const showPatientPortalReadOnlyAlert = isPatientPortal && (isVeradigmMedication || disabled);

  useEffect(() => {
    if (!showModal?.open) return;
    setShowAdditionalFields(checkIfWeNeedTheAdditionalFields(showModal?.data?.route));
  }, [showModal?.open, showModal?.data?.route]);

  const submitHandler = async (values) => {
    let clinicalNoteId = clinicalNote?.id;
    if (!clinicalNote?.id) {
      const cn = await saveNoteRef?.current?.checkPaymentBeforeSaving?.();

      clinicalNoteId = cn?.id;
    }

    if (isNonERXPrescription) {
      return returnValues(values);
    }

    let practiceId = null;
    if (patientIdProp) {
      practiceId = handleIsPatientCase({ values, patientIdProp, formik });
      if (!practiceId) return;
    }

    const copiedValues = structuredClone(values);
    const formattedMedData = formatMedicationDataForUpsert(copiedValues, fromWeightLoss);

    if (fromWeightLoss || formattedMedData?.weight_loss_medication_id) {
      const formattedWhlData = translateMedicationToWeightLossDatav2(formattedMedData);
      await upsertWeightLossSyncMutation.mutateAsync({
        patientId: narrativePatientId,
        appointmentId,
        clinicalNoteId,
        medication: { medications: [formattedMedData] },
        response: { row: [formattedWhlData] },
        type: 'weightLossAdministration',
        formId: formattedMedData?.weight_loss_medication_id || formik?.values?.cfr_id
      });
    } else {
      await upsertFormFunctionMutation.mutateAsync({
        patientId: narrativePatientId,
        clinicalNoteId,
        appointmentId,
        medication: { medications: [formattedMedData] },
        ...(patientIdProp && { practice_id: practiceId })
      });
    }
  };

  const formik = useFormik({
    initialValues: createObjectWithKeys(
      activeShowModal?.data,
      fromWeightLoss,
      showPreviewMedicationFromWhl,
      patientIdProp,
      isNonERXPrescription
    ),
    enableReinitialize: true,
    validationSchema: disabled || medicationReadOnly ? null : ProviderMedicationSchema,
    onSubmit: submitHandler
  });

  const handleCurrentUnitChange = (unit) => {
    formik.setFieldValue('unit', unit);
  };

  const upsertWeightLossSyncMutation = useMutation((data) => upsertWeightLossSync(navigate, data), {
    onSettled: async (data) => {
      const { code, newResponse, error } = data || {};
      if (code !== 0) {
        showAlert({
          title: 'Medication',
          message: error ?? "Couldn't create medication. Please contact support.",
          color: 'danger'
        });

        hideModal();
        return;
      }

      const queryData = queryClient.getQueryData([
        'aggregateNarrative',
        narrativePatientId,
        clinicalNote?.id,
        'medications'
      ]);

      const noKnown = queryData?.data?.narrative?.no_known;

      if (narrativePatientId && noKnown) {
        await setAggregateNarrativeState({
          queryClient,
          keys: [narrativePatientId, clinicalNote?.id, 'medications'],
          payload: {
            synced: true,
            no_known: false
          }
        });
      }

      if (formik.values.weight_loss_medication_id || formik.values.cfr_id) {
        setOverviewData((prev) => ({
          ...prev,
          weightLossAdministration: prev?.weightLossAdministration?.map((weightLoss) => {
            if (weightLoss?.id === newResponse?.id) {
              return newResponse;
            }
            return weightLoss;
          })
        }));
      } else {
        setOverviewData((prev) => ({
          ...prev,
          weightLossAdministration: [newResponse, ...iaRa(prev?.weightLossAdministration)]
        }));
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['medications'] }),
        queryClient.invalidateQueries({ queryKey: ['medication'] })
      ]);
      hideModal();
    }
  });

  const upsertFormFunctionMutation = useMutation((data) => createMedication(navigate, data), {
    onSettled: async (data) => {
      const { code, error } = data || {};

      if (code !== 0) {
        showAlert({
          title: 'Medication',
          message: error ?? "Couldn't create medication. Please contact support.",
          color: 'danger'
        });

        hideModal();
        return;
      }

      const noKnown = await queryClient.getQueryData([
        'aggregateNarrative',
        narrativePatientId,
        clinicalNote?.id,
        'medications'
      ])?.data?.narrative?.no_known;

      if (narrativePatientId && noKnown) {
        await setAggregateNarrativeState({
          queryClient,
          keys: [narrativePatientId, clinicalNote?.id, 'medications'],
          payload: {
            synced: true,
            no_known: false
          }
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['medications'] }),
        queryClient.invalidateQueries({ queryKey: ['medication'] })
      ]);
      hideModal();
    }
  });

  const mutateDeleteMedication = useMutation({
    mutationFn: ({ clinicalNoteId }) =>
      deleteMedication(navigate, {
        medicationId: fromWeightLoss
          ? activeShowModal?.data?.medication_id
          : activeShowModal?.data?.id,
        version: activeShowModal?.data?.version,
        clinicalNoteId,
        appointmentId
      }),
    onSuccess: async ({ code, error }) => {
      if (code !== 0) {
        showAlert({
          title: 'Medications',
          message: error ?? GENERIC_ERROR,
          color: 'warning'
        });
        return;
      }

      showAlert({
        title: 'Medication',
        message: 'Medication deleted',
        color: 'success'
      });

      queryClient.invalidateQueries(['medications']);
      queryClient.invalidateQueries(['medication']);
    }
  });

  const mutatateDeleteCustomFormResponse = useMutation({
    mutationFn: ({ clinicalNoteId }) =>
      deleteCustomFormResponse(navigate, {
        customFormId:
          activeShowModal?.data?.weight_loss_medication_id || activeShowModal?.data?.cfr_id,
        customFormVersion: activeShowModal?.data?.version,
        clinicalNoteId,
        appointmentId
      }),
    onSuccess: async ({ code, error }) => {
      if (code !== 0) {
        showAlert({
          title: 'Delete medication',
          message: error ?? GENERIC_ERROR,
          color: 'warning'
        });
        return;
      }

      const idToDelete =
        activeShowModal?.data?.weight_loss_medication_id || activeShowModal?.data?.cfr_id;
      setOverviewData((prev) => ({
        ...prev,
        weightLossAdministration: prev?.weightLossAdministration?.filter(
          (weightLoss) => weightLoss?.id !== idToDelete
        )
      }));
      queryClient.invalidateQueries(['medications']);
      queryClient.invalidateQueries(['medication']);
    }
  });

  const onHandleDelete = async () => {
    let clinicalNoteId = clinicalNote?.id;
    if (!clinicalNote?.id) {
      const cn = await saveNoteRef?.current?.checkPaymentBeforeSaving?.();

      clinicalNoteId = cn?.id;
    }

    if (activeShowModal?.data?.medication_id || (activeShowModal?.data?.id && !fromWeightLoss)) {
      await mutateDeleteMedication.mutateAsync({ clinicalNoteId });
    }
    if (activeShowModal?.data?.weight_loss_medication_id || activeShowModal?.data?.cfr_id) {
      await mutatateDeleteCustomFormResponse.mutateAsync({ clinicalNoteId });
    }

    hideModal();
    setConfirmOpen(false);
  };

  const handleCloseFromMedicationPreviewFromWeightLoss = () => {
    hideModal();
    setShowPreviewMedicationFromWhl(false);
  };

  const handleSelectMedication = async (medication) => {
    formik.setFieldValue('medication', capitalize(medication?.generic_name));
    if (medication?.product_ndc) {
      formik.setFieldValue('product_ndc', medication?.product_ndc);
    }
    if (medication?.product_id) {
      formik.setFieldValue('product_ndc_id', medication?.product_id);
    }
    if (medication?.route) {
      formik.setFieldValue('route', medication?.route[0]);
    }
  };

  const checkIfWeNeedTheAdditionalFields = (selectedRoute) => {
    return injectionRoutes.includes(selectedRoute);
  };

  const formBody = (
    <form>
      {showPatientPortalReadOnlyAlert && (
        <Alert
          title="Read-only medication"
          message={PATIENT_PORTAL_MEDICATION_READ_ONLY_MESSAGE}
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && isVeradigmMedication && (
        <Alert
          title="Read-only medication"
          message={VERADIGM_MEDICATION_READ_ONLY_MESSAGE}
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && !isVeradigmMedication && medicationReadOnly && (
        <Alert
          title="Restricted: read-only data access"
          message="Editing this area is currently restricted. Contact your internal systems admin if needed."
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      {!isPatientPortal && !isVeradigmMedication && disabled && patientIdProp && (
        <Alert
          title="Restricted: read-only data access"
          message="This is a medication created by the provider, so it can't be modified by you."
          color="warning"
          width="full"
          className="!mb-4"
        />
      )}
      <div>
        <label className="text-sm font-500 text-neutral-800">
          Is this medication currently active or inactive?
        </label>
        <span className="text-danger-500">*</span>

        <div className="!mt-3 flex gap-4">
          <RadioButton
            wrapperClassName="w-max"
            label="Active medication"
            onChange={() => formik.setFieldValue('status', MEDICATION_STATUS.ACTIVE)}
            isSelected={formik.values?.status === MEDICATION_STATUS.ACTIVE}
            disabled={disabled || medicationReadOnly}
          />
          <RadioButton
            wrapperClassName="w-max"
            label="Inactive medication"
            onChange={() => formik.setFieldValue('status', MEDICATION_STATUS.INACTIVE)}
            isSelected={formik.values?.status === MEDICATION_STATUS.INACTIVE}
            disabled={disabled || medicationReadOnly}
          />
        </div>

        {formik.errors?.status && (
          <p className="!pt-2 text-left text-sm text-danger-500">{formik.errors?.status}</p>
        )}
      </div>
      <div className="relative !mt-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <SelectMedicationTable
          medication={formik?.values?.medication}
          handleSelectMedication={handleSelectMedication}
          tableClassName="top-[76px]"
          required
          errorInput={formik?.errors?.medication}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="NDC"
          name="product_ndc"
          value={formik?.values?.product_ndc}
          onChange={formik.handleChange}
          disabled={disabled || medicationReadOnly}
        />
      </div>

      <div className="mt-4">
        <Textarea
          isEditor
          label="SIG"
          name="sig"
          value={formik?.values?.sig}
          onChange={(event) => formik.setFieldValue('sig', event)}
          className="min-h-[150px]"
          disabled={disabled || medicationReadOnly}
        />
      </div>

      <div className="mt-4 grid grid-cols-5 gap-4 md:grid-cols-2 sm:grid-cols-1">
        <div className="col-span-2 md:col-span-1 sm:col-span-1">
          <Input
            label="Unit"
            name="strength"
            value={formik?.values?.strength}
            onChange={(event) => formik.setFieldValue(`strength`, event?.target?.value)}
            units={UNIT_OPTIONS}
            currentUnit={formik?.values?.unit}
            onUnitChange={handleCurrentUnitChange}
            unitPanelClassName="left-0 !z-[9999] h-[300px] overflow-y-scroll"
            error={formik?.errors?.strength || formik?.errors?.unit}
            disabled={disabled || medicationReadOnly}
          />
        </div>
        <Input
          label="Quantity"
          value={formik?.values?.quantity}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'quantity',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.quantity}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="Days supply"
          value={formik?.values?.days_supply}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'days_supply',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.days_supply}
          disabled={disabled || medicationReadOnly}
        />
        <Input
          label="Refills"
          value={formik?.values?.refills}
          type="number"
          onChange={(event) =>
            formik.setFieldValue(
              'refills',
              event?.target?.value ? Number(event?.target?.value) : null
            )
          }
          error={formik?.errors?.refills}
          disabled={disabled || medicationReadOnly}
        />
      </div>
      <div className="!mt-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <Select
          label="Route"
          isCreatable={true}
          parentClassName="!mt-4"
          onChange={(e) => {
            const selectedRoute = e?.value;
            formik.setFieldValue('route', selectedRoute);
            const shouldShowAdditionalFields = checkIfWeNeedTheAdditionalFields(selectedRoute);
            setShowAdditionalFields(shouldShowAdditionalFields);
          }}
          value={{
            value: formik?.values?.route,
            label: formik?.values?.route
          }}
          options={medicationRouteOptions.map((route) => {
            return {
              value: route,
              label: route
            };
          })}
          placeholder="Select route"
          disabled={disabled || medicationReadOnly}
          isClearable={false}
        />
        {showAdditionalFields && (
          <Input
            className="!mt-4"
            label="Location"
            placeholder="Add location"
            value={formik?.values?.location}
            onChange={(event) => formik.setFieldValue('location', event.target.value)}
            disabled={disabled}
          />
        )}
        {fromWeightLoss && (
          <Input
            label="Weight"
            className="!mt-4"
            placeholder="Add weight"
            value={formik?.values?.weight}
            onChange={(event) => formik.setFieldValue('weight', event.target.value)}
            disabled={disabled}
            rightText="lbs"
          />
        )}

        <Select
          inputId="select-condition"
          label="Condition"
          parentClassName="!mt-4"
          id="condition"
          value={formik?.values?.condition}
          name="condition"
          onChange={(event) => formik?.setFieldValue('condition', event)}
          isCreatable
          isClearable
          isMulti
          components={{ DropdownIndicator: null }}
          placeholder="Type and select condition"
          disabled={disabled || medicationReadOnly}
        />
        <SearchProviders
          label="Prescriber"
          id="prescribingPhysician"
          parentClassName="!mt-4"
          placeholder="Prescriber"
          value={formik?.values?.prescriber}
          onChange={(event) => formik?.setFieldValue('prescriber', event)}
          disabled={disabled || medicationReadOnly}
        />

        <DateInputPicker
          label="Start date"
          parentClassName="!mt-4"
          onChange={(date) => formik.setFieldValue('start_date', date || null)}
          value={formik.values?.start_date}
          error={formik?.errors?.start_date}
          disabled={disabled || medicationReadOnly}
        />
        {formik.values?.status === MEDICATION_STATUS.INACTIVE && (
          <DateInputPicker
            label="End date"
            parentClassName="!mt-4"
            onChange={(date) => formik.setFieldValue('end_date', date || null)}
            value={formik.values?.end_date}
            error={formik?.errors?.end_date}
            disabled={disabled || medicationReadOnly}
          />
        )}
        {patientIdProp && (
          <Select
            label="Practice"
            parentClassName="!mt-4 w-full"
            isClearable={false}
            value={getPrefilledPatientPractice({
              data: practiceOptions,
              formik
            })}
            name="practice_id"
            error={formik?.errors?.practice_id}
            onChange={(event) => formik?.setFieldValue('practice_id', event?.value)}
            options={getPatientPracticeOptions(practiceOptions)}
            placeholder="Select practice"
            required={true}
            disabled={disabled || medicationReadOnly}
          />
        )}
      </div>
      <div className="mt-4">
        <Textarea
          isEditor
          label={
            <div className="flex gap-1">
              <span className="text-sm font-500 text-neutral-800">Note</span>
              <span className="test-sm text-neutral-500">(Optional)</span>
            </div>
          }
          name="note"
          value={formik?.values?.note}
          onChange={(event) => formik.setFieldValue('note', event)}
          className="min-h-[150px]"
          disabled={disabled || medicationReadOnly}
        />
      </div>
    </form>
  );

  if (renderInline) return formBody;

  return (
    <Modal
      width="medium"
      isOpen={showModal?.open}
      handleClose={
        showPreviewMedicationFromWhl ? handleCloseFromMedicationPreviewFromWeightLoss : hideModal
      }
      title={modalTitle}
      isFooter={diffDosView}
      footer={
        <div className="flex w-full items-center justify-between">
          <Button
            variant="tertiary"
            text={isVeradigmMedication ? 'Close' : 'Cancel'}
            onClick={
              showPreviewMedicationFromWhl
                ? handleCloseFromMedicationPreviewFromWeightLoss
                : hideModal
            }
          />
          {!isVeradigmMedication && (
            <div className="flex items-center gap-2">
              {(formik?.values?.id || formik?.values?.cfr_id) && (
                <>
                  <Button
                    icon="trash-mono"
                    variant="tertiary"
                    iconColor="black"
                    isDisabled={disabled || mutateDeleteMedication?.isLoading || medicationReadOnly}
                    className="h-[40px] w-[40px]"
                    onClick={() => setConfirmOpen(true)}
                  />
                  <Confirm
                    icon="info-filled"
                    iconColor="danger"
                    iconSize={42}
                    variant="danger"
                    title="Delete medication"
                    message={`Are you sure you want to delete this medication? ${getMedicationDeleteContextMessage(formik?.values)}`}
                    isOpen={!!confirmOpen}
                    handleClose={() => setConfirmOpen(false)}
                    footerActions={{
                      primary: {
                        text: 'Delete',
                        onClick: onHandleDelete,
                        color: 'danger',
                        isLoading:
                          mutateDeleteMedication?.isLoading ||
                          mutatateDeleteCustomFormResponse?.isLoading,
                        isDisabled:
                          mutateDeleteMedication?.isLoading ||
                          mutatateDeleteCustomFormResponse?.isLoading
                      }
                    }}
                  />
                </>
              )}
              <Button
                text={formik?.values?.id || formik?.values?.cfr_id ? 'Update' : 'Create'}
                data-qa="upsert-btn"
                onClick={() => formik?.submitForm()}
                isLoading={
                  upsertFormFunctionMutation?.isLoading || upsertWeightLossSyncMutation?.isLoading
                }
                isDisabled={
                  upsertFormFunctionMutation?.isLoading ||
                  upsertWeightLossSyncMutation?.isLoading ||
                  disabled ||
                  medicationReadOnly
                }
              />
            </div>
          )}
        </div>
      }>
      {formBody}
    </Modal>
  );
};

export default UpsertProviderMedication;
